import { v4 as uuidv4 } from 'uuid';
import shadowRoot from 'react-shadow';
import autoBind from 'react-autobind';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import TimelineActivity from '~/components/drawers/timeline/activity';
import APIRequest from '~/lib/api_request';
import { EMAIL_QUOTES_STYLES, checkEmailQuotesExist, processEmailBody } from '~/lib/EmailUtils';
import ExpandThreadButton from '~/components/shared/email/ExpandThreadButton';
import EmailDetailsButton from '~/components/shared/email/EmailDetailsButton';

class TimelineActivityEmailSent extends TimelineActivity {
  emailBodyRef = React.createRef();

  containerRef = React.createRef();

  abortController = new AbortController();

  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      ...this.state,
      expandedView:       false,
      loadingEmailEvents: false,
      emailEvents:        null,
      showDetails:        false,
    };
  }

  componentDidMount() {
    this.loadEmailEvents();
    this.checkQuotesExist();
  }

  componentWillUnmount() {
    this.abortController.abort();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.activity !== this.state.activity
      || prevState.expandedView !== this.state.expandedView
    ) {
      this.checkQuotesExist();
    }
  }

  loadEmailEvents() {
    const { activity } = this.props;
    const { email } = activity;

    if (!email || this.state.emailEvents) {
      this.setState({ loadingEmailEvents: false });
      return;
    }

    if (email.source !== 'manual' && email.source !== 'bcc' && email.source !== 'forward') {
      this.setState({ loadingEmailEvents: true });

      APIRequest.get({
        resource: `/v1/events/${email.id}`,
        signal:   this.abortController.signal,
      })
        .end((_err, response) => {
          if (!this.abortController.signal.aborted) {
            if (response && response.ok) {
              this.setState({
                emailEvents:        response.body,
                loadingEmailEvents: false,
              });
            } else {
              this.setState({
                loadingEmailEvents: false,
                emailEvents:        null,
              });
            }
          }
        });
    }
  }

  onTogglePopover() {
    this.setState({
      popoverOpen: !this.state.popoverOpen,
    });
  }

  toggleExpandedView() {
    this.setState((prevState) => ({ expandedView: !prevState.expandedView }));
  }

  checkQuotesExist() {
    const emailBodyElement = this.emailBodyRef.current;

    if (!emailBodyElement) {
      return;
    }

    const quotesExist = checkEmailQuotesExist(emailBodyElement);

    if (this.state.quotesExist !== quotesExist) {
      this.setState({ quotesExist });
    }
  }

  setEmailBodyRef(element) {
    this.emailBodyRef.current = element;
    if (element) {
      this.checkQuotesExist();
    }
  }

  renderDetailsButton() {
    const { activity } = this.state;
    return <EmailDetailsButton email={activity?.email} containerRef={this.containerRef} />;
  }

  renderEmailEvents() {
    const { loadingEmailEvents, emailEvents } = this.state;
    const { activity } = this.props;
    const { email } = activity;

    if (!email || email.source === 'manual' || email.source === 'bcc' || email.source === 'forward') {
      return null;
    }

    const containerClasses = 'd-flex align-items-center justify-content-end mt-2 px-2';

    if (loadingEmailEvents) {
      return (
        <div className={containerClasses} style={{ height: '40px' }}>
          <FontAwesomeIcon icon="far fa-spinner" pulse />
        </div>
      );
    }

    if (!emailEvents) return null;

    return (
      <div className={containerClasses} style={{ height: '40px' }}>
        <div className="mr-2 text-center text-black">
          <strong className="text-uppercase">Status</strong>
          <br />
          {emailEvents?.status}
        </div>
        <div className="mr-2 text-center text-black">
          <strong className="text-uppercase">Opens</strong>
          <br />
          {emailEvents?.opened}
        </div>
        <div className="text-center text-black">
          <strong className="text-uppercase">Clicks</strong>
          <br />
          {emailEvents?.clicked}
        </div>
      </div>
    );
  }

  renderView() {
    const { activity, expandedView, quotesExist } = this.state;
    let subject; let body; let attachments; let source; let
      id;

    if (activity.email) {
      id = activity.email.id;
      subject = activity.email.subject;
      body = activity.email.body;
      attachments = activity.email.attachments;
      source = activity.email.source;
    } else {
      body = activity.body;
    }

    return (
      <div id={`${id}-Email`}>
        {subject && <h5 className="mb5">{subject}</h5>}

        <shadowRoot.div
          id="stepEmailBodyPreview"
          className="form-control font-activity"
        >
          <style>{EMAIL_QUOTES_STYLES}</style>
          <div
            ref={this.setEmailBodyRef}
            className={`email-body ${expandedView ? 'expanded' : ''}`}
            dangerouslySetInnerHTML={{ __html: processEmailBody(body) }}
          />
        </shadowRoot.div>

        {quotesExist && (
          <div>
            <ExpandThreadButton
              expanded={expandedView}
              onClick={this.toggleExpandedView}
            />
          </div>
        )}

        {attachments && attachments.map((a) => (
          <React.Fragment key={uuidv4()}>
            <a href={a.url}>
              <FontAwesomeIcon icon={['far', 'fa-file']} className="mt10 mr5" />
              {a.name}
            </a>
            <br />
          </React.Fragment>
        ))}

        {this.renderEmailEvents()}
      </div>
    );
  }
}

TimelineActivityEmailSent.defaultProps = {
  editable:         false,
  deletable:        true,
  title:            'Email Sent',
  icon:             'envelope',
  prefix:           'fas',
  color:            'text-blue',
  alwaysPublic:     false,
  showReplyButtons: true,
};

export default TimelineActivityEmailSent;
